import Segmented from "@/components/Segmented";
import { useEffect, useMemo, useRef, useState } from "react";
import { http } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarClock, Inbox, Gavel, Clock3, ArrowUpRight, Plus, Check } from "lucide-react";
import NewEventDialog from "@/components/NewEventDialog";
import InfoTicker from "@/components/InfoTicker";
import AvisosPainel from "@/components/AvisosPainel";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { confirmBaixa } from "@/components/ConfirmBaixa";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import LastUpdatedLabel from "@/components/LastUpdatedLabel";
import SyncHealthBanner from "@/components/SyncHealthBanner";
import SharedInbox from "@/components/SharedInbox";
import PrazosDiaNaoUtilBanner from "@/components/PrazosDiaNaoUtilBanner";
import PublicacoesSplitButton from "@/components/PublicacoesSplitButton";
import BackupAviso from "@/components/BackupAviso";
import ProxyDjenAviso from "@/components/ProxyDjenAviso";
import CompromissosLista from "@/components/CompromissosLista";
import { AlertasPainel, PrioritariosCard, RevisoesCard, RevisaoBloco, Proximos7 } from "@/components/PainelAcoes";
import { buildActions, addDaysIso, itemLabel } from "@/lib/painel";
import { pickTips, bumpOpening, FAST_OPENINGS, weekdayName } from "@/lib/letreiro";

// Lote 79 — quando repetir a fase completa do letreiro: 1ª abertura do dia OU novidade
// (publicação/andamento capturado) desde a última vez que o Painel foi visto.
const LS_FULL_DAY = "iapauta_painel_full_day";
const LS_LAST_VIEW = "iapauta_painel_last_view";
const lsGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* sem storage: ok */ } };

// Data local (não UTC) no formato ISO usado pelo backend, para comparar com hearing.date
// e destacar os compromissos marcados para hoje.
const todayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Lote 97: "Ciente" num conflito de horário — guardado só neste aparelho; chaves de datas passadas são descartadas.
const LS_ACK_CONFLICTS = "iapauta_conflitos_ciente";
function readAcks() {
  try {
    const arr = JSON.parse(localStorage.getItem(LS_ACK_CONFLICTS) || "[]");
    const hoje = todayISO();
    return Array.isArray(arr) ? arr.filter((k) => typeof k === "string" && k.split("|")[0] >= hoje) : [];
  } catch { return []; }
}
// Lote 102: "Ciente" num prazo/compromisso de HOJE — só limpa o alerta vermelho (letreiro e bloco
// Alertas); o prazo continua pendente nas listas e no atalho. Vale até a baixa do prazo. Só neste aparelho.
const LS_ACK_URGENT = "iapauta_urgente_ciente";
function readUrgentAcks() {
  try { const a = JSON.parse(localStorage.getItem(LS_ACK_URGENT) || "[]"); return Array.isArray(a) ? a.filter((k) => typeof k === "string") : []; } catch { return []; }
}
function writeUrgentAcks(list) { try { localStorage.setItem(LS_ACK_URGENT, JSON.stringify(list.slice(-200))); } catch { /* sem storage */ } }
function writeAcks(list) { try { localStorage.setItem(LS_ACK_CONFLICTS, JSON.stringify(list)); } catch { /* sem storage */ } }

// Rótulo curto da área do processo, mostrado junto das partes na tabela — mesma
// convenção de valores usada em NewEventDialog.jsx/ReviewQueue.jsx (CASE_AREAS).
const CASE_AREA_LABELS = {
  trabalhista: "Trabalhista",
  civel: "Cível",
  penal: "Penal",
  outro: "Outro",
};

// Fase da audiência (Lote 25) — mostrada junto do rótulo de área, quando preenchida.

// Saudação acompanha o horário do dia e o primeiro nome do usuário logado (Lote 50):
// antes das 12h "Bom dia", das 12h às 18h "Boa tarde", depois "Boa noite". Sem nome
// cadastrado (carregando, ou campo vazio), cai no texto genérico de antes.
const greeting = (fullName) => {
  const h = new Date().getHours();
  const period = h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
  const firstName = fullName?.trim()?.split(/\s+/)[0];
  // Lote 69: a frase "Segue sua agenda inteligente" saiu do Painel (fica só na barra do topo no celular).
  return firstName ? `${period}, ${firstName}` : period;
};

// Lote 71: `total` opcional — quando é maior que `value`, mostra "1 de 2" (as que já passaram do
// horário saem do número grande, mas o total do período continua visível ao lado).
const Stat = ({ label, value, total, icon: Icon, testid, tone = "slate", to, alert = false }) => (
  <Link
    to={to}
    data-testid={testid}
    data-alert={alert ? "1" : undefined}
    className={`block bg-white rounded-md p-4 md:p-6 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200 ${
      alert ? "border-2 border-gold-500 hover:border-gold-600" : "border border-stone-200 hover:border-stone-400"
    }`}
  >
    <div className="flex items-center justify-between mb-2 md:mb-4 gap-2">
      <span className={`text-[10px] md:text-[11px] font-bold tracking-[0.12em] md:tracking-[0.2em] uppercase ${alert ? "text-gold-800" : "text-stone-500"}`}>{label}</span>
      <Icon className={`w-4 h-4 ${alert ? "text-gold-600" : `text-${tone}-600`}`} strokeWidth={1.75} />
    </div>
    <div className={`text-3xl md:text-4xl font-bold tabular-nums tracking-tight text-stone-900`}>
      {value}
      {total != null && total > value && (
        <span className="ml-1.5 text-sm md:text-base font-medium text-stone-400 tracking-normal" data-testid={`${testid}-total`}>de {total}</span>
      )}
    </div>
  </Link>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, clients: 0 });
  const [upcoming, setUpcoming] = useState([]);
  const [unreadPubs, setUnreadPubs] = useState(0);
  const [resumo, setResumo] = useState(null);        // Lote 79: /painel/resumo
  const [startFull, setStartFull] = useState(null);  // decidido uma vez, na 1ª carga
  const [periodo, setPeriodo] = useState({ de: "", ate: "" });   // Lote 151: "Período" (aba Todos com datas)
  const [tipoFiltro, setTipoFiltro] = useState("todos");        // Lote 151: Todos os tipos | audiencia | prazo | reuniao
  const [range, setRange] = useState(null);          // "hoje" | "amanha" | "semana" | "todos"
  const firstLoad = useRef(true);
  // Lote 96: nº da abertura do Painel hoje (1ª e 2ª = letreiro mais ágil) e dicas desta abertura.
  const [openNo] = useState(() => bumpOpening(todayISO()));
  const [tips] = useState(() => pickTips(3));
  // Lote 97: relógio do Painel (atualiza a cada minuto) — tira "hoje às HH:MM" quando o horário passa.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNowTick(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  // Lote 97: conflitos marcados como "Ciente" (neste aparelho) — somem do card e do letreiro.
  const [ackedConflicts, setAckedConflicts] = useState(() => readAcks());
  const [ackedUrgent, setAckedUrgent] = useState(() => readUrgentAcks());
  // Lote 132: ciência (só hoje) das revisões/movimentações no letreiro, e janela "Ver todas as pendências".
  const [pendOpen, setPendOpen] = useState(null);   // null | "pend" | "rev" (Lote 137)

  const load = async () => {
    try {
      const since = firstLoad.current ? (lsGet(LS_LAST_VIEW) || undefined) : undefined;
      const [s, u, p, r] = await Promise.all([
        http.get("/hearings/stats"),
        http.get("/hearings/upcoming"),
        http.get("/publicacoes/stats"),
        http.get("/painel/resumo", { params: since ? { since } : {} }).catch(() => null),
      ]);
      setStats(s.data);
      setUpcoming(u.data);
      setUnreadPubs(p.data?.unread ?? 0);
      if (r?.data) setResumo(r.data);
      if (firstLoad.current) {
        firstLoad.current = false;
        const today = todayISO();
        const novidade = (r?.data?.news_since ?? 0) > 0;
        setStartFull(lsGet(LS_FULL_DAY) !== today || novidade);
        lsSet(LS_FULL_DAY, today);
        lsSet(LS_LAST_VIEW, new Date().toISOString());
      }
    } catch {
      /* handled by interceptor */
      if (firstLoad.current) { firstLoad.current = false; setStartFull(true); }
    }
  };

  useEffect(() => { load(); }, []);
  // Lote 57: atualiza sozinho ao voltar pro Painel e a cada 15min enquanto fica aberto.
  const { lastUpdated } = useAutoRefresh(load);

  // Lote 45f (pedido do Robson): marcar prazo como feito direto daqui do Painel — some
  // da lista assim que a resposta chega, sem precisar esperar o próximo carregamento
  // (o backend já exclui prazo_done de /hearings/upcoming, então um load() de novo
  // manteria consistente, mas o filtro local dá feedback imediato).
  const markPrazoDone = async (h) => {
    const ehPrazo = (h.hearing?.event_type || "audiencia") === "prazo";
    if (!(await confirmBaixa(itemLabel(h.hearing), ehPrazo ? "prazo" : "realizada"))) return;
    try {
      await http.post(`/publications/${h.pub_id}/mark-prazo-done`);
      setUpcoming((cur) => cur.filter((i) => i.pub_id !== h.pub_id));
      toast.success(ehPrazo ? "Prazo marcado como feito" : "Marcado como realizada", {
        duration: 6000,
        action: { label: "Desfazer", onClick: async () => {
          try { await http.post(`/publications/${h.pub_id}/unmark-prazo-done`); } catch { toast.error("Não consegui desfazer"); }
          load();
        } },
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar o prazo");
    }
  };

  // Mensagens do painel rotativo ("letreiro", InfoTicker) — a sincronização com o
  // DJEN/DataJud agora é só automática (6h e 16h — horário mudou no Lote 38, agendador
  // interno — sem botão manual), então esse painel é a forma do usuário acompanhar o
  // status sem precisar clicar em nada.
  //
  // Lote 45f (pedido do Robson, 23/09/2026): o "Essa semana: X audiências" antigo contava
  // a semana inteira (segunda a domingo), incluindo dias que já passaram — dava a entender
  // que ainda tinha X coisas por vir quando na verdade parte já tinha acontecido. Trocado
  // pra usar `upcoming` (mesma lista de "Próximos compromissos", que o backend já filtra
  // pra date >= hoje) em vez do contador `this_week_*` do /hearings/stats, tanto pra achar
  // a próxima audiência quanto pra identificar (não só contar) um conflito de horário.
  const isAudienciaType = (h) => (h?.event_type || "audiencia") === "audiencia";
  const minutesOf = (hhmm) => {
    if (!hhmm || !hhmm.includes(":")) return null;
    const [h, m] = hhmm.split(":").map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
  };
  const shortDate = (iso) => formatDate(iso).slice(0, 5); // "dd/mm/aaaa" -> "dd/mm"
  const sundayIso = (() => {
    const d = new Date();
    const day = d.getDay(); // 0=domingo, 1=segunda, ... 6=sábado
    d.setDate(d.getDate() + (day === 0 ? 0 : 7 - day));
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  })();
  // Lote 97: nada cujo horário já passou hoje entra no letreiro / card (a lista "Próximos compromissos" segue igual).
  const todayStrNow = todayISO();
  const nowMinutes = nowTick.getHours() * 60 + nowTick.getMinutes();
  const horarioPassou = (h) => {
    const t = minutesOf(h?.time);
    return h?.date === todayStrNow && t != null && t < nowMinutes;
  };
  const upcomingAudiencias = upcoming.filter((u) => isAudienciaType(u.hearing) && !horarioPassou(u.hearing));
  // Lote 131: conflito vale para audiências e demais compromissos; prazo fica de fora.
  const upcomingConflitaveis = upcoming.filter((u) => (u.hearing?.event_type || "audiencia") !== "prazo" && !horarioPassou(u.hearing));
  // Prazos de HOJE ainda sem baixa cujo horário passou: não são "ação agora", mas não podem sumir —
  // ficam no bloco vermelho de Alertas até a baixa.
  const hojeSemBaixa = upcoming
    .filter((u) => u.hearing?.event_type === "prazo" && horarioPassou(u.hearing))
    .map((u) => ({ pub_id: u.pub_id, days_late: 0, hearing: u.hearing }));
  const weekAudRemaining = upcomingAudiencias.filter((u) => u.hearing?.date <= sundayIso).length;
  const weekPrazoRemaining = upcoming.filter((u) => u.hearing?.event_type === "prazo" && u.hearing?.date <= sundayIso).length;
  const nextAudiencia = upcomingAudiencias[0]?.hearing;

  // Mesmo critério de conflito do backend (_count_conflicts/find_time_conflicts): só
  // audiências, mesmo dia, menos de 60min de diferença — aqui só pra poder NOMEAR o
  // conflito no letreiro (partes + horários), não só contar.
  const findConflictMessage = () => {
    const byDate = {};
    for (const u of upcomingConflitaveis) {
      const date = u.hearing?.date;
      const t = minutesOf(u.hearing?.time);
      if (!date || t == null) continue;
      (byDate[date] ||= []).push({ t, hearing: u.hearing, pubId: u.pub_id });
    }
    let anyPair = false;
    for (const date of Object.keys(byDate).sort()) {
      const list = byDate[date].sort((a, b) => a.t - b.t);
      for (let i = 0; i < list.length - 1; i++) {
        if (list[i + 1].t - list[i].t < 60) {
          anyPair = true;
          const key = `${date}|${list[i].pubId}|${list[i + 1].pubId}`;
          if (ackedConflicts.includes(key)) continue;   // "Ciente": segue para o próximo conflito
          const a = list[i].hearing;
          const b = list[i + 1].hearing;
          const labelA = a.parties || a.case_number || a.description || "um compromisso";
          const labelB = b.parties || b.case_number || b.description || "outro compromisso";
          return {
            text: `⚠️ Conflito de horário dia ${shortDate(date)}: ${labelA} às ${a.time} e ${labelB} às ${b.time} — toque para ver os dois`,
            to: `/calendario?data=${date}&conflito=${list[i].pubId},${list[i + 1].pubId}`, key, anyPair,
          };
        }
      }
    }
    return { text: null, anyPair };
  };

  // Lote 96 — o letreiro é a voz da IA do app: primeiro o que exige ação (ata, hoje/amanhã,
  // conflito, validar), depois o que ela informa, por último dicas. Quase tudo é clicável (1 toque).
  const conflictInfo = findConflictMessage();       // { text, to, key, anyPair }
  const conflict = conflictInfo.text ? conflictInfo : null;
  const conflictMessage = conflict?.text || null;
  const conflictsCount = stats.conflicts_count ?? 0;
  const atas = resumo?.atas_pendentes || [];
  const desde = resumo?.desde_ultima_visita || {};
  const tickerMessages = [];

  // 1) lembra
  const diaAta = (iso) => `${weekdayName(iso)} (${shortDate(iso)})`;
  atas.slice(0, 2).forEach((a) => {
    tickerMessages.push({
      text: `Lembrete: a audiência de ${a.parties || a.case_number || "um processo"} foi ${diaAta(a.date)}. Já baixou a ata?`,
      to: `/audiencia/${a.pub_id}`,
      ciente: true,
    });
  });
  const tomorrow = addDaysIso(todayISO(), 1);
  upcomingAudiencias.filter((u) => u.hearing?.date === tomorrow).slice(0, 2).forEach((u) => {
    const h = u.hearing;
    const comarca = h.court_district || h.court;
    tickerMessages.push({
      text: `Amanhã${h.time ? ` às ${h.time.slice(0, 5)}` : ""}: audiência ${h.parties || h.case_number || ""}${comarca ? `, em ${comarca}` : ""}`.replace(/\s+,/, ","),
      to: `/audiencia/${u.pub_id}`,
      ciente: true,
    });
  });
  // 2) alerta
  if (conflict) {
    tickerMessages.push({ ...conflict, ciente: true });
  } else if (conflictsCount > 0 && !conflictInfo.anyPair) {   // todos "Ciente" → não repete pela contagem
    tickerMessages.push({ text: `⚠️ ${conflictsCount} compromisso${conflictsCount > 1 ? "s" : ""} com conflito de horário — toque para ver a agenda`, to: "/calendario", ciente: true });
  }
  const toValidate = stats.to_validate ?? stats.pending ?? 0;
  if (toValidate > 0) {
    tickerMessages.push({ text: `Valide as audiências: ${toValidate} aguardando em “Para validar”`, to: "/revisao", ciente: true });
  }
  if (unreadPubs > 0) {
    tickerMessages.push({ text: `${unreadPubs} publicaç${unreadPubs > 1 ? "ões" : "ão"} não lida${unreadPubs > 1 ? "s" : ""} na caixa de entrada`, to: "/publicacoes", ciente: true });
  }
  // 3) informa
  if (desde.publicacoes > 0) {
    const v = desde.viraram_audiencia || 0;
    tickerMessages.push({
      text: `Li ${desde.publicacoes} publicaç${desde.publicacoes > 1 ? "ões novas" : "ão nova"} desde sua última visita${v ? `; ${v} ${v > 1 ? "viraram audiência" : "virou audiência"}` : ""}.`,
      to: "/publicacoes",
    });
  }
  if (desde.agrupadas > 0) {
    tickerMessages.push(`Agrupei ${desde.agrupadas} comunicaç${desde.agrupadas > 1 ? "ões repetidas" : "ão repetida"} do mesmo ato, sem duplicar sua agenda.`);
  }
  if (stats.last_sync_at) {
    const hh = String(new Date(stats.last_sync_at).getHours()).padStart(2, "0");
    tickerMessages.push(`Última atualização às ${hh}h`);
  }
  if (weekAudRemaining || weekPrazoRemaining) {
    const partes = [];
    if (weekAudRemaining) partes.push(`${weekAudRemaining} audiência${weekAudRemaining > 1 ? "s" : ""}`);
    if (weekPrazoRemaining) partes.push(`${weekPrazoRemaining} prazo${weekPrazoRemaining > 1 ? "s" : ""}`);
    tickerMessages.push({ text: `Ainda essa semana: ${partes.join(" e ")}`, ciente: true });
  } else {
    tickerMessages.push("Nada mais agendado para essa semana");
  }
  if (nextAudiencia) {
    const dateLabel = nextAudiencia.date === todayISO() ? "hoje" : `dia ${shortDate(nextAudiencia.date)}`;
    const comarca = nextAudiencia.court_district || nextAudiencia.court;
    const nextPub = upcomingAudiencias[0]?.pub_id;
    tickerMessages.push({
      text: `Sua próxima audiência é ${dateLabel}${nextAudiencia.time ? ` às ${nextAudiencia.time}` : ""}${comarca ? `, na comarca de ${comarca}` : ""}`,
      to: nextPub ? `/audiencia/${nextPub}` : undefined,
      ciente: true,
    });
  }
  if (toValidate === 0) tickerMessages.push("Tudo validado por aqui ✓");
  tickerMessages.push("Atualizações automáticas do DJEN/DataJud às 6h e 16h");
  // 4) ensina (2 a 3 dicas novas a cada abertura)
  tips.forEach((t) => tickerMessages.push(t));

  // Lote 79 — ações em ordem de prioridade (vencido > hoje > conflito > validar/revisar) —
  // alimentam o card "Ação agora" e a fase 2 do letreiro.
  const overdue = resumo?.overdue || [];
  const reviewTotal = resumo?.review?.total || 0;
  const actions = buildActions({
    overdue, overdueOlder: resumo?.overdue_older || 0, upcoming,
    conflictMessage, conflictTo: conflict?.to, conflictKey: conflict?.key || null, atas, toValidate, reviewTotal, ackedPubIds: ackedUrgent, now: nowTick,
  });
  // Lote 102 — o antigo card "Ação agora" virou a mensagem VERMELHA do letreiro: só o que é urgente
  // (prazo de hoje sem baixa, audiência nas próximas 2 h, conflito de horário de hoje).
  // Lote 137 — o letreiro NÃO traz mais os avisos de prazo/audiência urgente nem os de revisão (nem os botões deles): isso vive
  // no quadro "Pendências / Revisão" abaixo dos atalhos. O letreiro só chama a atenção (mensagem vermelha, ver `avisoTicker`).
  // Saem da fase 2 as ações de prazo de hoje e de "publicações para revisar", já cobertas pelo quadro.
  const actionTickerMsgs = actions.filter((a) => !(a.key === "today" && a.canDone) && a.key !== "review").map((a) => ({ text: a.title, to: a.to, ciente: true }));
  const fullTickerMsgs = tickerMessages;
  // Lote 137 — dois grupos de aviso: PENDÊNCIAS (vermelho: prazos sem baixa, conflito, alteração de colega) e REVISÃO (azul:
  // revisão periódica de processo e processo ★ com movimentação nova). Nada some: o quadro abaixo dos atalhos os mostra,
  // revezando 1 min cada, e o letreiro só avisa que existem.
  const hojeSemBaixaVisiveis = hojeSemBaixa.filter((o) => !ackedUrgent.includes(o.pub_id));
  const pendVencidos = overdue.length + hojeSemBaixaVisiveis.length;
  const revisoesTodas = [...(resumo?.revisoes || []), ...(resumo?.revisoes_escritorio || [])];      // Lote 164: inclui as de contas de colegas
  const pendRevisoes = revisoesTodas.length;
  const pendPrios = (resumo?.prioritarios || []).length;
  const pendConflito = conflictMessage ? 1 : 0;
  const pendCompart = (resumo?.compartilhados || []).length;
  const totalPend = pendVencidos + pendConflito + pendCompart;
  const totalRev = pendRevisoes + pendPrios;
  const partesPend = [
    pendVencidos ? `${pendVencidos} prazo${pendVencidos > 1 ? "s" : ""} sem baixa` : null,
    pendConflito ? "1 conflito de horário" : null,
    pendCompart ? `${pendCompart} alteraç${pendCompart > 1 ? "ões" : "ão"} de colega` : null,
  ].filter(Boolean).join(" · ");
  const partesRev = [
    pendRevisoes ? (pendRevisoes > 1 ? `${pendRevisoes} revisões periódicas` : "1 revisão periódica") : null,
    pendPrios ? `${pendPrios} com movimentação nova` : null,
  ].filter(Boolean).join(" · ");
  const irParaAvisos = () => { try { document.getElementById("painel-avisos")?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* sem scroll */ } };
  const avisoTexto = [
    totalPend ? `${totalPend} pendência${totalPend > 1 ? "s" : ""}` : null,
    totalRev ? (totalRev > 1 ? `${totalRev} revisões` : "1 revisão") : null,
  ].filter(Boolean).join(" e ");
  const avisoTicker = avisoTexto ? { text: `Atenção: você tem ${avisoTexto} — veja o quadro abaixo dos atalhos`, tone: "red", onClick: irParaAvisos, ciente: true } : null;
  const fullComAviso = avisoTicker ? [avisoTicker, ...fullTickerMsgs] : fullTickerMsgs;
  // Na fase 2 (2ª abertura do dia em diante) o aviso só entra se houver OUTRAS ações para alternar com ele; sozinho a fase 2
  // ficaria parada nele. Sem ações, o letreiro segue na fase completa (onde o aviso é a 1ª frase de cada rodada).
  const actionsComAviso = avisoTicker && actionTickerMsgs.length > 0 ? [avisoTicker, ...actionTickerMsgs] : actionTickerMsgs;
  const tickerKey = JSON.stringify([fullComAviso, actionsComAviso]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tickerData = useMemo(() => ({ full: fullComAviso, actions: actionsComAviso }), [tickerKey]);

  // Lote 97 — baixa em 1 toque; todas avisam com "Desfazer" por 6 s.
  const undoToast = (msg, undo) => toast.success(msg, { duration: 6000, action: { label: "Desfazer", onClick: undo } });
  const donePrazoById = async (pubId, label) => {
    if (!(await confirmBaixa(label || ""))) return;
    try {
      await http.post(`/publications/${pubId}/mark-prazo-done`);
      setUpcoming((cur) => cur.filter((i) => i.pub_id !== pubId));
      undoToast("Prazo marcado como feito", async () => {
        try { await http.post(`/publications/${pubId}/unmark-prazo-done`); } catch { toast.error("Não consegui desfazer"); }
        load();
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar o prazo");
    }
  };
  const ackConflict = (key) => {
    if (!key) return;
    const next = [...ackedConflicts, key];
    setAckedConflicts(next); writeAcks(next);
    undoToast("Conflito marcado como ciente", () => {
      const cur = readAcks().filter((k) => k !== key);
      setAckedConflicts(cur); writeAcks(cur);
    });
  };
  const cienteUrgent = (pubId) => {
    if (!pubId) return;
    const next = [...ackedUrgent.filter((k) => k !== pubId), pubId];
    setAckedUrgent(next); writeUrgentAcks(next);
    undoToast("Alerta limpo — o prazo continua pendente", () => {
      const cur = readUrgentAcks().filter((k) => k !== pubId);
      setAckedUrgent(cur); writeUrgentAcks(cur);
    });
  };
  const ackAta = async (action, motivo) => {
    const id = action.pendenciaId;
    try {
      await http.post(`/prazos/${id}/ata-aviso`, { motivo });
      setResumo((cur) => cur ? { ...cur, atas_pendentes: (cur.atas_pendentes || []).filter((a) => a.pendencia_id !== id) } : cur);
      undoToast(motivo === "dispensada" ? "Aviso da ata dispensado" : "Ata marcada como baixada", async () => {
        try { await http.post(`/prazos/${id}/ata-aviso/desfazer`); } catch { toast.error("Não consegui desfazer"); }
        load();
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao dar baixa na ata");
    }
  };
  const baixar = (action, b) => {
    if (b.kind === "prazo") return donePrazoById(action.pubId, action.label);
    if (b.kind === "ata") return ackAta(action, b.label === "Dispensar" ? "dispensada" : "baixada");
    if (b.kind === "conflito") return ackConflict(action.conflictKey);
    return null;
  };

  const cienciaCompartilhado = async (changeId) => {
    try {
      await http.post(`/painel/compartilhado/${changeId}/ciencia`);
      setResumo((r) => r ? { ...r, compartilhados: (r.compartilhados || []).filter((x) => x.change_id !== changeId) } : r);
    } catch { toast.error("Não consegui registrar a ciência. Tente de novo."); }
  };

  // Filtro da lista "Próximos compromissos": Hoje | Amanhã | Semana (hoje + 6 dias) | Todos.
  const todayStr = todayISO();
  const inRange = (h, r) => {
    const d = h.hearing?.date || "";
    if (r === "hoje") return d === todayStr;
    if (r === "amanha") return d === addDaysIso(todayStr, 1);
    if (r === "semana") return d >= todayStr && d <= addDaysIso(todayStr, 6);
    if (periodo.de && d < periodo.de) return false;
    if (periodo.ate && d > periodo.ate) return false;
    return true;
  };
  const doTipo = (h) => tipoFiltro === "todos" || (h.hearing?.event_type || "audiencia") === tipoFiltro;
  const rangeCounts = {
    hoje: upcoming.filter((h) => doTipo(h) && inRange(h, "hoje")).length,
    amanha: upcoming.filter((h) => doTipo(h) && inRange(h, "amanha")).length,
    semana: upcoming.filter((h) => doTipo(h) && inRange(h, "semana")).length,
    todos: upcoming.filter((h) => doTipo(h) && inRange(h, "todos")).length,
  };
  const activeRange = range || "hoje";   // Lote 98: o Painel sempre abre em "Hoje" (pedido do Robson, 22/10/2026)
  const visibleUpcoming = upcoming.filter((h) => doTipo(h) && inRange(h, activeRange));


  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto">
      <header className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4 md:gap-6">
        <div className="min-w-0">
          <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-1 md:mb-2">
            Painel
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-stone-900 tracking-tight">
            {greeting(user?.name)}
          </h1>
        </div>
        <div className="flex flex-col md:items-end gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <NewEventDialog
              onCreated={load}
              trigger={
                <Button
                  data-testid="new-event-button"
                  variant="outline"
                  size="sm"
                  className="gap-2 border-stone-300 hover:bg-stone-100"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} /> Novo evento
                </Button>
              }
            />
            <PublicacoesSplitButton unread={unreadPubs} />
          </div>
          <LastUpdatedLabel date={lastUpdated} />
        </div>
      </header>

      <div className="flex flex-col">
        {/* Lote 64: só aparece quando a busca de publicações falhou/parou/ficou dias sem nada novo. */}
        <div className="order-1"><SyncHealthBanner variant="painel" /></div>
        <div className="order-1"><BackupAviso /><ProxyDjenAviso /></div>
        {/* Lote 75: compromissos que outro usuário do IApauta compartilhou com você. */}
        <div className="order-1"><SharedInbox onChange={() => load()} /></div>
        {/* Lote 98: prazos caindo em sábado/domingo/feriado → próximo dia útil (só aparece se houver). */}
        <div className="order-1"><PrazosDiaNaoUtilBanner onFixed={() => load()} /></div>

        {/* Lote 70: o quadro de alertas (Ação agora / Próxima audiência / Conflito) ficou só na
            aba Agenda; aqui o letreiro e os 4 contadores voltam ao topo, inclusive no celular. */}
        <div className="order-2 mb-4 md:mb-6">
          {startFull !== null && (
            <InfoTicker messages={tickerData.full} actionMessages={tickerData.actions} startFull={startFull} fast={openNo <= FAST_OPENINGS} />
          )}
        </div>

        {/* Lote 139: "Pergunte à sua agenda" — perguntas curtas com resposta pronta (sem IA), logo abaixo do letreiro. */}

        {/* Lote 132: alertas, ★ e revisão periódica saíram do meio do Painel — falam no letreiro e ficam aqui, numa linha fina. */}
        <div className="order-4 mb-4 md:mb-6 empty:hidden">
          <AvisosPainel
            pend={{ total: totalPend, partes: partesPend }}
            rev={{ total: totalRev, partes: partesRev }}
            onOpen={(k) => setPendOpen(k)}
          />
        </div>
        <Dialog open={!!pendOpen} onOpenChange={(o) => setPendOpen(o ? pendOpen : null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="painel-pendencias-dialog" data-aviso={pendOpen || ""}>
            <DialogHeader>
              <DialogTitle>{pendOpen === "rev" ? "Revisão" : "Pendências"}</DialogTitle>
              <DialogDescription>
                {pendOpen === "rev"
                  ? "Revisões periódicas de processos e movimentações novas em processos prioritários."
                  : "Prazos sem baixa, conflitos de horário e alterações feitas por colegas."}
              </DialogDescription>
            </DialogHeader>
            {pendOpen === "pend" && (
              <div className="space-y-4">
                <AlertasPainel
                  overdue={overdue}
                  overdueOlder={resumo?.overdue_older || 0}
                  conflictMessage={conflictMessage}
                  conflictTo={conflict?.to}
                  onAckConflict={conflict?.key ? () => ackConflict(conflict.key) : undefined}
                  hojeSemBaixa={hojeSemBaixaVisiveis}
                  onCiente={cienteUrgent}
                  onDone={donePrazoById}
                  onChanged={load}
                />
                {pendCompart > 0 && (
                  <section data-testid="painel-compartilhados" className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h3 className="font-display text-base font-semibold text-blue-900">Alterações de colegas</h3>
                    <ul className="mt-2 space-y-2">
                      {(resumo?.compartilhados || []).map((it) => (
                        <li key={it.change_id} className="flex items-start gap-2 text-sm text-blue-900">
                          <span className="flex-1 min-w-0 break-words">{it.texto}</span>
                          <Button size="sm" variant="outline" className="h-8 shrink-0 border-blue-300 bg-white text-blue-800" data-testid={`compartilhado-ciencia-${it.change_id}`} onClick={() => cienciaCompartilhado(it.change_id)}>Ciência</Button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            )}
            {pendOpen === "rev" && (
              <div className="space-y-4">
                {pendPrios > 0 && (
                  <div data-testid="painel-prioritarios-wrap">
                    <PrioritariosCard items={resumo.prioritarios}
                      onVisto={(id) => setResumo((r) => r ? { ...r, prioritarios: (r.prioritarios || []).filter((x) => x.processo_id !== id) } : r)} />
                  </div>
                )}
                {pendRevisoes > 0 && (
                  <div data-testid="painel-revisoes-wrap">
                    <RevisoesCard items={revisoesTodas}
                      onRevisado={(id) => setResumo((r) => r ? { ...r, revisoes: (r.revisoes || []).filter((x) => x.processo_id !== id), revisoes_escritorio: (r.revisoes_escritorio || []).filter((x) => x.processo_id !== id) } : r)} />
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div className="order-3 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6" data-testid="painel-contadores">
          <Stat label="Para validar" value={stats.to_validate ?? stats.pending} icon={Inbox} testid="stat-pending" tone="amber" to="/revisao" />
          {/* Fim de semana: "semana"/"hoje" não fazem sentido (sábado e domingo não têm
              expediente) — troca pra "próxima semana"/"segunda-feira", usando os campos
              calculados no backend (is_weekend/next_week_audiencias/monday_audiencias). */}
          {stats.is_weekend ? (
            <>
              <Stat label="Audiências da próxima semana" value={stats.next_week_audiencias ?? 0} icon={CalendarClock} testid="stat-week-audiencias" tone="gold" to="/audiencias?filter=audiencias" />
              <Stat label="Audiência de segunda-feira" value={stats.monday_audiencias ?? 0} icon={Gavel} testid="stat-today-audiencias" tone="gold" to="/audiencias?filter=audiencias" />
            </>
          ) : (
            <>
              <Stat label="Audiências da semana" value={stats.this_week_audiencias ?? 0} total={stats.this_week_audiencias_total} icon={CalendarClock} testid="stat-week-audiencias" tone="gold" to="/audiencias?filter=audiencias" />
              <Stat label="Audiências hoje" value={stats.today_audiencias ?? 0} total={stats.today_audiencias_total} icon={Gavel} testid="stat-today-audiencias" tone="gold" to="/audiencias?filter=audiencias" />
            </>
          )}
          <Stat label="Prazos hoje" value={stats.today_prazos ?? 0} icon={Clock3} testid="stat-today-prazos" tone="slate" to="/audiencias?filter=prazos" alert={(stats.today_prazos ?? 0) > 0} />
        </div>

        <section className="order-6 mb-4 md:mb-6 md:bg-white md:border md:border-stone-200 md:rounded-md" data-testid="painel-proximos">
          <div className="flex items-center justify-between md:px-6 md:py-4 md:border-b md:border-stone-200 mb-3 md:mb-0">
            <div>
              <h2 className="font-display text-xl font-semibold text-stone-900">Próximos compromissos</h2>
              <p className="hidden md:block text-xs text-stone-500 mt-0.5">Audiências, reuniões e prazos aprovados com data futura.</p>
            </div>
            <Link to="/audiencias" className="text-xs font-semibold text-stone-500 hover:text-stone-900 flex items-center gap-1">
              Ver todos <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>

          {/* Lote 79: Hoje / Amanhã / Semana / Todos (abre em Hoje; sem nada hoje, em Semana). */}
          <div className="mb-3 md:mx-6 md:mt-3">
            <Segmented value={activeRange} onChange={setRange} testid="proximos-range"
              options={[["hoje", "Hoje"], ["amanha", "Amanhã"], ["semana", "Semana"], ["todos", "Período"]].map(([k, label]) => ({
                key: k, label, count: rangeCounts[k] > 0 ? rangeCounts[k] : null, testid: `range-${k}`,
              }))} />
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-stone-600" data-testid="proximos-filtros">
              <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} data-testid="filtro-tipo" aria-label="Filtrar por tipo"
                className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs">
                <option value="todos">Todos os tipos</option>
                <option value="audiencia">Audiências</option>
                <option value="prazo">Prazos</option>
                <option value="reuniao">Compromissos</option>
              </select>
              {activeRange === "todos" && (
                <span className="inline-flex items-center gap-1.5" data-testid="periodo-datas">
                  de <input type="date" value={periodo.de} onChange={(e) => setPeriodo((p) => ({ ...p, de: e.target.value }))} data-testid="periodo-de" className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" />
                  até <input type="date" value={periodo.ate} onChange={(e) => setPeriodo((p) => ({ ...p, ate: e.target.value }))} data-testid="periodo-ate" className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" />
                  {(periodo.de || periodo.ate) && <button type="button" className="underline" onClick={() => setPeriodo({ de: "", ate: "" })}>limpar</button>}
                </span>
              )}
            </div>
          </div>

          {upcoming.length === 0 ? (
            <div className="p-12 text-center">
              <CalendarClock className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.25} />
              <p className="font-display text-lg text-stone-900 mb-1">Nenhum compromisso agendado</p>
              <p className="text-sm text-stone-500 max-w-sm mx-auto">
                Depois de aprovar publicações na fila de revisão elas aparecem aqui.
              </p>
            </div>
          ) : visibleUpcoming.length === 0 ? (
            <div className="py-8 text-center" data-testid="proximos-vazio">
              <p className="font-display text-lg text-stone-900 mb-1">
                {activeRange === "hoje" ? "Nada agendado para hoje" : activeRange === "amanha" ? "Nada agendado para amanhã" : "Nada agendado nesse período"}
              </p>
              <div className="flex items-center justify-center gap-4">
                {activeRange === "hoje" && rangeCounts.semana > 0 && (
                  <button type="button" data-testid="proximos-ver-semana" className="text-sm font-semibold text-emerald-brand hover:underline" onClick={() => setRange("semana")}>
                    Ver a semana
                  </button>
                )}
                <button type="button" className="text-sm font-semibold text-emerald-brand hover:underline" onClick={() => setRange("todos")}>
                  Ver todos os próximos
                </button>
              </div>
            </div>
          ) : (
            <>
              <CompromissosLista items={visibleUpcoming} hoje={todayStr} userName={user?.name} onToggleDone={markPrazoDone} />
            </>
          )}
        </section>

        <div className="order-7 mb-4 md:mb-6"><RevisaoBloco review={resumo?.review} /></div>
        <div className="order-8 mb-2"><Proximos7 next7={resumo?.next7} /></div>
      </div>
    </div>
  );
}
